import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { request, type Page } from '@waago/api'
import type { ServiceActivity } from '@waago/ui'

/**
 * "Activité actuelle" — the switcher in the AF app header (frame `4650:4369`).
 *
 * <p>It is a **screen filter**, not a dispatch gate. A provider who offers both
 * transport and livraison is offered both whatever this says; what it changes is
 * which missions, demandes and paiements the screens list, so somebody running
 * two lines of business can look at one of them at a time.
 *
 * <p>Deciding otherwise — making it decide what dispatch offers — would mean a
 * provider silently stops being offered livraison because they left the pill on
 * transport three days ago. Availability is the control for that, and it already
 * exists on the dashboard.
 *
 * <p>The choice is per-device (`localStorage`): it is a way of looking at the
 * screen, not a property of the account.
 */
const STORAGE_KEY = 'waago.provider.activity'

/** `null` is the frame's implicit first option: every activity at once. */
export type CurrentActivity = ServiceActivity | null

type Ctx = {
  activity: CurrentActivity
  setActivity: (a: CurrentActivity) => void
  /** The activities this provider actually has services for, in catalogue order. */
  options: ServiceActivity[]
}

const ORDER: ServiceActivity[] = ['TRANSPORT_PERSON', 'DELIVERY', 'EQUIPMENT_RENTAL', 'DRIVER_HIRE']

const CurrentActivityContext = createContext<Ctx>({
  activity: null,
  setActivity: () => {},
  options: [],
})

function read(): CurrentActivity {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return ORDER.includes(stored as ServiceActivity) ? (stored as ServiceActivity) : null
  } catch {
    // Private browsing, or storage the user has switched off. The switcher
    // still works for the session; it just does not remember.
    return null
  }
}

export function CurrentActivityProvider({ children }: { children: ReactNode }) {
  const [activity, setStored] = useState<CurrentActivity>(read)

  const services = useQuery({
    queryKey: ['me', 'services', 'activities'],
    queryFn: () => request<Page<{ activity: string }>>('/me/services?limit=100'),
    staleTime: 60_000,
  })

  const options = useMemo(() => {
    const mine = new Set((services.data?.data ?? []).map((s) => s.activity))
    return ORDER.filter((a) => mine.has(a))
  }, [services.data])

  const setActivity = useCallback((next: CurrentActivity) => {
    setStored(next)
    try {
      if (next === null) localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // See read(): remembering is a convenience, not the feature.
    }
  }, [])

  // A stored activity the provider no longer offers would filter every screen
  // down to nothing with no way to tell why, so it reads as "toutes".
  const effective = activity !== null && options.includes(activity) ? activity : null

  const value = useMemo(
    () => ({ activity: options.length > 1 ? effective : null, setActivity, options }),
    [effective, options, setActivity],
  )

  return <CurrentActivityContext value={value}>{children}</CurrentActivityContext>
}

export function useCurrentActivity(): Ctx {
  return use(CurrentActivityContext)
}
